import { createSlice } from '@reduxjs/toolkit';
import {
  fetchCompetitionByMarket,
  fetchCompetitionByEvent,
  resolveCompetitionAlias,
  persistCompetitionAlias,
  resetCompetitionAlias,
  fetchLinkedCompetition,
} from '../services/competition';

const statusIdle = 'idle';
const statusLoading = 'loading';
const statusSucceeded = 'succeeded';
const statusFailed = 'failed';

const initialState = {
  byMarket: {
    status: statusIdle,
    alias: '',
    data: null,
    error: null,
    lastFetched: null,
  },
  byEvent: {
    status: statusIdle,
    eventId: '',
    data: null,
    error: null,
    lastFetched: null,
  },
  resolver: {
    status: statusIdle,
    data: null,
    error: null,
    lastRun: null,
  },
  aliasPersist: {
    status: statusIdle,
    data: null,
    error: null,
  },
  linked: {
    status: statusIdle,
    params: null,
    rows: [],
    summary: null,
    error: null,
    lastFetched: null,
  },
};

const competitionSlice = createSlice({
  name: 'competition',
  initialState,
  reducers: {
    clearCompetitionState: () => ({ ...initialState }),
    resetResolverState: (state) => {
      state.resolver = { ...initialState.resolver };
    },
    resetAliasPersistState: (state) => {
      state.aliasPersist = { ...initialState.aliasPersist };
    },
    clearLinkedCompetition: (state) => {
      state.linked = { ...initialState.linked };
    },
  },
  extraReducers: (builder) => {
    builder
      // by market
      .addCase(fetchCompetitionByMarket.pending, (state, action) => {
        const alias = action.meta?.arg?.alias?.trim() || '';
        state.byMarket.status = statusLoading;
        state.byMarket.error = null;
        state.byMarket.alias = alias;
        state.byMarket.lastFetched = null;
      })
      .addCase(fetchCompetitionByMarket.fulfilled, (state, action) => {
        state.byMarket.status = statusSucceeded;
        state.byMarket.alias = action.payload.alias;
        state.byMarket.data = action.payload.data;
        state.byMarket.error = null;
        state.byMarket.lastFetched = Date.now();
      })
      .addCase(fetchCompetitionByMarket.rejected, (state, action) => {
        state.byMarket.status = statusFailed;
        state.byMarket.error = action.error.message || 'Failed to fetch competition data';
        state.byMarket.data = null;
        state.byMarket.lastFetched = Date.now();
      })

      // by event
      .addCase(fetchCompetitionByEvent.pending, (state, action) => {
        const eventId = action.meta?.arg?.eventId?.trim() || '';
        state.byEvent.status = statusLoading;
        state.byEvent.error = null;
        state.byEvent.eventId = eventId;
        state.byEvent.lastFetched = null;
      })
      .addCase(fetchCompetitionByEvent.fulfilled, (state, action) => {
        state.byEvent.status = statusSucceeded;
        state.byEvent.eventId = action.payload.eventId;
        state.byEvent.data = action.payload.data;
        state.byEvent.error = null;
        state.byEvent.lastFetched = Date.now();
      })
      .addCase(fetchCompetitionByEvent.rejected, (state, action) => {
        state.byEvent.status = statusFailed;
        state.byEvent.error = action.error.message || 'Failed to fetch event competition data';
        state.byEvent.data = null;
        state.byEvent.lastFetched = Date.now();
      })

      // resolver
      .addCase(resolveCompetitionAlias.pending, (state) => {
        state.resolver.status = statusLoading;
        state.resolver.error = null;
        state.resolver.lastRun = null;
      })
      .addCase(resolveCompetitionAlias.fulfilled, (state, action) => {
        state.resolver.status = statusSucceeded;
        state.resolver.data = action.payload.data;
        state.resolver.error = null;
        state.resolver.lastRun = Date.now();
      })
      .addCase(resolveCompetitionAlias.rejected, (state, action) => {
        state.resolver.status = statusFailed;
        state.resolver.error = action.error.message || 'Resolver failed';
        state.resolver.data = null;
        state.resolver.lastRun = Date.now();
      })

      // persist alias
      .addCase(persistCompetitionAlias.pending, (state) => {
        state.aliasPersist.status = statusLoading;
        state.aliasPersist.error = null;
        state.aliasPersist.data = null;
      })
      .addCase(persistCompetitionAlias.fulfilled, (state, action) => {
        state.aliasPersist.status = statusSucceeded;
        state.aliasPersist.data = action.payload.data;
        state.aliasPersist.error = null;
      })
      .addCase(persistCompetitionAlias.rejected, (state, action) => {
        state.aliasPersist.status = statusFailed;
        state.aliasPersist.error = action.error.message || 'Failed to persist alias';
        state.aliasPersist.data = null;
      })

      // alias reset hook
      .addCase(resetCompetitionAlias.pending, (state) => {
        state.aliasPersist.status = statusLoading;
        state.aliasPersist.error = null;
      })
      .addCase(resetCompetitionAlias.fulfilled, (state, action) => {
        state.aliasPersist.status = statusSucceeded;
        state.aliasPersist.data = action.payload.data;
        state.aliasPersist.error = null;
      })
      .addCase(resetCompetitionAlias.rejected, (state, action) => {
        state.aliasPersist.status = statusFailed;
        state.aliasPersist.error = action.error.message || 'Failed to reset alias';
      })

      // linked competition
      .addCase(fetchLinkedCompetition.pending, (state) => {
        state.linked.status = statusLoading;
        state.linked.error = null;
      })
      .addCase(fetchLinkedCompetition.fulfilled, (state, action) => {
        state.linked.status = statusSucceeded;
        state.linked.params = action.payload.params;
        state.linked.rows = action.payload.data?.rows || [];
        state.linked.summary = {
          totalEvents: action.payload.data?.total_events,
          totalCompetitors: action.payload.data?.total_competitors,
          markets: action.payload.data?.markets,
        };
        state.linked.error = null;
        state.linked.lastFetched = Date.now();
      })
      .addCase(fetchLinkedCompetition.rejected, (state, action) => {
        state.linked.status = statusFailed;
        state.linked.error = action.error.message || 'Failed to fetch linked competition';
        state.linked.rows = [];
        state.linked.summary = null;
        state.linked.lastFetched = Date.now();
      });
  },
});

export const {
  clearCompetitionState,
  resetResolverState,
  resetAliasPersistState,
  clearLinkedCompetition,
} = competitionSlice.actions;

export default competitionSlice.reducer;
